import React, { useState, useCallback } from 'react';
import { faixaDoAtraso, consequenciaContratual } from '../utils/scoreFinanceiro';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, Users, FileText, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { beneficiariosAPI, atendimentosAPI, inadimplenciaAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AvisoDefasagem from '../components/AvisoDefasagem';

const Busca = () => {
  const [searchType, setSearchType] = useState('beneficiario'); // 'beneficiario' ou 'protocolo'
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [alertas, setAlertas] = useState({}); // {matricula_soul: {dias, mensalidade}}
  const [alertasErp, setAlertasErp] = useState({}); // {matricula: {qtd, bloqueia}}
  const [loading, setLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newBeneficiario, setNewBeneficiario] = useState({
    nome_completo: '',
    cpf: '',
    numero_matricula: '',
    telefone_principal: '',
    email: '',
    plano_contratado: '',
  });

  const navigate = useNavigate();
  const { user } = useAuth();

  // Debounce para busca
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Alerta de inadimplência na busca (Sit. 12 do levantamento da AME): o
  // atendente precisa ver a situação financeira ANTES de abrir a ficha.
  // Chave = matrícula SOUL (mat_resp_fin); uma única chamada em lote para
  // todos os resultados, em vez de N requisições.
  const buscarAlertasInadimplencia = async (lista) => {
    const mats = (lista || [])
      .map((r) => r.matricula_soul)
      .filter(Boolean);
    if (!mats.length) { setAlertas({}); setAlertasErp({}); return; }
    const unicas = [...new Set(mats)];
    try {
      const { data } = await inadimplenciaAPI.checkLote(unicas);
      setAlertas(data?.resultados || {});
    } catch (_) {
      setAlertas({}); // alerta é um plus: falhando, a busca segue normal
    }
    // Alertas do ERP (Sit. 13) — só o marcador; o texto fica na ficha.
    try {
      const { data } = await beneficiariosAPI.alertasLote(unicas);
      setAlertasErp(data?.resultados || {});
    } catch (_) {
      setAlertasErp({});
    }
  };

  const performSearch = async (term) => {
    if (term.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      if (searchType === 'beneficiario') {
        const response = await beneficiariosAPI.search(term);
        setResults(response.data);
        buscarAlertasInadimplencia(response.data);
      } else {
        // Busca por protocolo
        try {
          const response = await atendimentosAPI.buscarProtocolo(term);
          setResults(response.data); // Agora retorna array
        } catch (error) {
          if (error.response?.status === 404) {
            setResults([]);
          } else {
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      setResults([]);
    }
    setLoading(false);
  };

  const debouncedSearch = useCallback(debounce(performSearch, 300), [searchType]);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    debouncedSearch(term);
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setSearchTerm('');
    setResults([]);
  };

  const handleSelectResult = (result) => {
    if (searchType === 'beneficiario') {
      // Navegar para a pagina de detalhes usando o CPF
      const cpf = result.cpf || result.cpf_mascarado?.replace(/\D/g, '');
      if (cpf) {
        navigate(`/beneficiario-detalhe/${cpf}`);
      } else {
        navigate(`/beneficiario/${result.id}`);
      }
    } else {
      navigate(`/atendimento/${result.id}`);
    }
  };

  const handleCreateBeneficiario = async (e) => {
    e.preventDefault();

    try {
      const response = await beneficiariosAPI.create(newBeneficiario);
      toast.success('Beneficiario cadastrado com sucesso!');
      setShowNewForm(false);
      setNewBeneficiario({
        nome_completo: '',
        cpf: '',
        numero_matricula: '',
        telefone_principal: '',
        email: '',
        plano_contratado: '',
      });
      navigate(`/beneficiario/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar beneficiario');
    }
  };

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  return (
    <div className="search-container">
      {/* Idade do dado: a busca lê a cópia local; se ela parou de sincronizar,
          um beneficiário recente não aparece aqui e ninguém saberia por quê. */}
      <AvisoDefasagem />

      <div className="search-box">
        <h2 style={{ marginBottom: '1rem', color: 'var(--gray-800)' }}>
          Buscar
        </h2>

        {/* Tipo de busca */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            className={`btn ${searchType === 'beneficiario' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handleSearchTypeChange('beneficiario')}
          >
            <Users size={16} />
            Beneficiário
          </button>
          <button
            className={`btn ${searchType === 'protocolo' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handleSearchTypeChange('protocolo')}
          >
            <FileText size={16} />
            Protocolo
          </button>
        </div>

        <div className="search-input-wrapper">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            className="search-input"
            placeholder={
              searchType === 'beneficiario'
                ? 'Digite o CPF ou nome do beneficiário...'
                : 'Digite o número do protocolo...'
            }
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        {loading && (
          <div className="loading" style={{ padding: '1rem' }}>
            <div className="spinner" style={{ width: '24px', height: '24px' }}></div>
          </div>
        )}
      </div>

      {/* Resultados */}
      {results.length > 0 && (
        <div className="search-results">
          {results.map((result, index) => (
            <div
              key={result.id || `result-${index}`}
              className="search-result-item"
              onClick={() => handleSelectResult(result)}
            >
              {searchType === 'beneficiario' ? (
                <>
                  <div className="result-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="result-name" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>{result.nome_completo}</span>
                      {result.nome_social && (
                        <span title="Nome Social (Lei 12.852/2013 · ANS RN 459/2021)" style={{ fontSize: '0.75rem', color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{ fontWeight: 700, opacity: 0.55, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nome Social</span>
                          {result.nome_social}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {/* Alerta do ERP (Sit. 13): na lista só sinaliza que
                          existe — o texto aparece na ficha e no protocolo. */}
                      {(() => {
                        const al = alertasErp[result.matricula_soul];
                        if (!al || !al.qtd) return null;
                        const c = al.bloqueia ? '#dc2626' : '#d97706';
                        return (
                          <span
                            title={al.bloqueia
                              ? 'Beneficiário com alerta de bloqueio no ERP. Abra a ficha antes de atender'
                              : 'Beneficiário com alerta registrado no ERP'}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              background: `${c}18`, border: `1px solid ${c}60`, color: c,
                              borderRadius: '10px', padding: '2px 8px', fontSize: '0.68rem',
                              fontWeight: 700, whiteSpace: 'nowrap',
                            }}
                          >
                            <AlertCircle size={11} />
                            {al.qtd > 1 ? `${al.qtd} alertas` : 'alerta'}
                          </span>
                        );
                      })()}

                      {/* Alerta de inadimplência — mesma regra de faixa da
                          ficha do beneficiário (utils/scoreFinanceiro). */}
                      {(() => {
                        const alerta = alertas[result.matricula_soul];
                        if (!alerta || !alerta.dias) return null;
                        const f = faixaDoAtraso(alerta.dias);
                        return (
                          <span
                            title={`Inadimplente há ${alerta.dias} dias${consequenciaContratual(alerta.dias, alerta.mensalidade)}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              background: `${f.cor}18`, border: `1px solid ${f.cor}60`,
                              color: f.cor, borderRadius: '10px', padding: '2px 8px',
                              fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap',
                            }}
                          >
                            <AlertCircle size={11} />
                            {alerta.dias}d em atraso
                          </span>
                        );
                      })()}
                      <span className={`status-badge status-${result.status}`}>
                        {result.status?.toUpperCase()}
                      </span>
                      {result.possui_contratos_inativos && (
                        <div
                          className="inactive-indicator"
                          style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          title={result.contratos_inativos?.map(c =>
                            `${c.plano || 'S/Plano'} (${c.contrato || 'S/Contrato'}) - ${c.status}`
                          ).join('\n')}
                        >
                          <AlertCircle
                            size={16}
                            style={{ color: '#f39c12', cursor: 'help' }}
                          />
                          <span style={{
                            fontSize: '11px',
                            color: '#f39c12',
                            marginLeft: '2px'
                          }}>
                            inativo
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="result-details">
                    <span>CPF: {result.cpf_mascarado}</span>
                    {result.celular && <span>Cel: {result.celular}</span>}
                    {result.telefone_fixo && <span>Fixo: {result.telefone_fixo}</span>}
                    {!result.celular && !result.telefone_fixo && result.telefone_principal && (
                      <span>Tel: {result.telefone_principal}</span>
                    )}
                    {result.numero_matricula && (
                      <span>Matrícula: {result.numero_matricula}</span>
                    )}
                    {result.plano_contratado && (
                      <span>Plano: {result.plano_contratado}</span>
                    )}
                  </div>
                  {result.possui_contratos_inativos && result.contratos_inativos?.length > 0 && (
                    <div
                      className="inactive-contracts"
                      style={{
                        marginTop: '8px',
                        padding: '8px',
                        background: '#fff8e1',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#856404'
                      }}
                    >
                      <strong>Histórico:</strong>{' '}
                      {result.contratos_inativos.map((c, i) => (
                        <span key={i}>
                          {c.plano || 'S/Plano'} ({c.contrato || 'S/Contrato'})
                          {i < result.contratos_inativos.length - 1 ? ' | ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="result-name">
                    <FileText size={18} style={{ marginRight: '8px', display: 'inline' }} />
                    {result.protocolo}
                  </div>
                  <div className="result-details">
                    <span>Beneficiário: {result.beneficiario?.nome_completo}</span>
                    <span>Setor: {result.setor?.nome}</span>
                    <span className={`status-badge status-${result.status}`}>
                      {result.status}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Nenhum resultado */}
      {searchTerm.length >= 2 && !loading && results.length === 0 && (
        <div className="search-results">
          <div className="empty-state">
            {searchType === 'beneficiario' ? <Users size={48} /> : <FileText size={48} />}
            <p>
              {searchType === 'beneficiario'
                ? 'Nenhum beneficiário encontrado'
                : 'Nenhum protocolo encontrado'}
            </p>
            {searchType === 'beneficiario' && user?.perfil === 'administrador' && (
              <button
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
                onClick={() => setShowNewForm(true)}
              >
                <UserPlus size={18} />
                Cadastrar Novo
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal Novo Beneficiario */}
      {showNewForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Cadastrar Beneficiario</h3>
              <button className="modal-close" onClick={() => setShowNewForm(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateBeneficiario}>
              <div className="form-group">
                <label>Nome Completo *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newBeneficiario.nome_completo}
                  onChange={(e) =>
                    setNewBeneficiario({ ...newBeneficiario, nome_completo: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>CPF *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newBeneficiario.cpf}
                  onChange={(e) =>
                    setNewBeneficiario({ ...newBeneficiario, cpf: formatCPF(e.target.value) })
                  }
                  maxLength="14"
                  required
                />
              </div>
              <div className="form-group">
                <label>Numero Matricula</label>
                <input
                  type="text"
                  className="form-control"
                  value={newBeneficiario.numero_matricula}
                  onChange={(e) =>
                    setNewBeneficiario({ ...newBeneficiario, numero_matricula: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Telefone</label>
                <input
                  type="text"
                  className="form-control"
                  value={newBeneficiario.telefone_principal}
                  onChange={(e) =>
                    setNewBeneficiario({ ...newBeneficiario, telefone_principal: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={newBeneficiario.email}
                  onChange={(e) =>
                    setNewBeneficiario({ ...newBeneficiario, email: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Plano Contratado</label>
                <input
                  type="text"
                  className="form-control"
                  value={newBeneficiario.plano_contratado}
                  onChange={(e) =>
                    setNewBeneficiario({ ...newBeneficiario, plano_contratado: e.target.value })
                  }
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewForm(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-success">
                  Cadastrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Busca;
